import type { Budget, CalcOption, versionInfo } from "../types/budget/budget";
import type {
  BudgetServiceValuePhase,
  CreateBudgetServiceV2Body,
  PatchBudgetServicesBatchOperation,
} from "../types/budget/budget-service-v2";
import type { ServiceData, ServiceValues } from "../types/budget/service-item";
import { buildServiceIfMatch } from "./service-etag";

/** BE-P3-05 max ops per batch request. */
export const BUDGET_SERVICES_BATCH_MAX_OPS = 50;

/**
 * FE-P3-04 move strategy (documented):
 * BE `PATCH /v2/budget/:id/services/:serviceId` only deep-merges phase
 * `values` — it does **not** accept group-identity fields (`calcOption`,
 * `type` / category, `supplierId`). Cross-group moves therefore use
 * **delete + create** (POST new line, DELETE old id), not group-field PATCH.
 */
export const V2_SERVICE_MOVE_STRATEGY = "delete+create" as const;

export type ResolvedServiceValuePhase = {
  valuePhase: BudgetServiceValuePhase;
  /** Present only when `valuePhase` is `negotiation` (index = version - 1). */
  negotiationVersion?: number;
};

/**
 * Maps editor `versionInfo.status` to v2 batch `valuePhase` (FE-P3-03).
 * - OPEN → budget
 * - NEGOTIATE → negotiation + negotiationVersion
 * - APPROVED → closing
 *
 * Unknown / other statuses fall back to budget (safe default for OPEN-like tabs).
 */
export function resolveServiceValuePhase(
  versionInfo: Pick<versionInfo, "version" | "status">
): ResolvedServiceValuePhase {
  const status = (versionInfo?.status || "OPEN").toUpperCase();

  if (status === "APPROVED") {
    return { valuePhase: "closing" };
  }

  if (status === "NEGOTIATE") {
    return {
      valuePhase: "negotiation",
      negotiationVersion: versionInfo.version,
    };
  }

  return { valuePhase: "budget" };
}

/**
 * Reads the phase snapshot currently edited for a service line.
 */
export function getPhaseValuesFromService(
  service: ServiceData,
  versionInfo: Pick<versionInfo, "version" | "status">
): ServiceValues | undefined {
  const { valuePhase, negotiationVersion } =
    resolveServiceValuePhase(versionInfo);

  if (valuePhase === "closing") {
    return service.closingValues;
  }

  if (valuePhase === "negotiation") {
    const index = (negotiationVersion ?? versionInfo.version) - 1;
    if (index < 0) return undefined;
    return service.negotiationValue?.[index];
  }

  return service.budgetValues;
}

/**
 * Replaces dirty-map values with the current phase snapshot from `services`.
 *
 * `UPDATE_CALC_VALUES` payloads carry new tax/exchange flags but still-stale
 * `taxData` — the reducer recalculates after the action is applied. Export
 * reads persisted services, so flushing the pre-recalc snapshot produced
 * spreadsheets that missed the last TaxesModal save until a full PUT.
 *
 * Returns a new Map used as the flush snapshot. Also updates `dirtyMap` in
 * place with the same object references so the post-flush
 * `dirtyMap.get(id) === flushedValues` check still detects mid-flight edits.
 */
export function refreshDirtyPhaseValuesFromBudget(
  dirtyMap: Map<string, Partial<ServiceValues>>,
  services: ServiceData[] | undefined,
  versionInfo: Pick<versionInfo, "version" | "status">
): Map<string, Partial<ServiceValues>> {
  const snapshot = new Map(dirtyMap);
  if (!services?.length) return snapshot;

  const byId = new Map<string, ServiceData>();
  for (const service of services) {
    if (service?._id) byId.set(String(service._id), service);
  }

  for (const serviceId of Array.from(snapshot.keys())) {
    const service = byId.get(serviceId);
    if (!service) continue;
    const fresh = getPhaseValuesFromService(service, versionInfo);
    if (!fresh) continue;
    snapshot.set(serviceId, fresh);
    if (dirtyMap.has(serviceId)) {
      dirtyMap.set(serviceId, fresh);
    }
  }

  return snapshot;
}

/**
 * Builds batch PATCH ops from a dirty map (latest values win per serviceId).
 * Does not chunk — use {@link chunkBatchOperations} for the 50-op BE limit.
 *
 * When `updatedAtByServiceId` has an entry for a service, attaches `ifMatch`
 * (FE-P3-05 / BE-P3-04). Missing entries omit `ifMatch` (last-write-wins).
 */
export function buildBatchOperations(
  dirtyMap:
    | Map<string, Partial<ServiceValues>>
    | Record<string, Partial<ServiceValues>>,
  versionInfo: Pick<versionInfo, "version" | "status">,
  updatedAtByServiceId?:
    | Map<string, string>
    | Record<string, string | undefined>
): PatchBudgetServicesBatchOperation[] {
  const { valuePhase, negotiationVersion } =
    resolveServiceValuePhase(versionInfo);

  const entries =
    dirtyMap instanceof Map
      ? Array.from(dirtyMap.entries())
      : Object.entries(dirtyMap);

  const lookupUpdatedAt = (serviceId: string): string | undefined => {
    if (!updatedAtByServiceId) return undefined;
    if (updatedAtByServiceId instanceof Map) {
      return updatedAtByServiceId.get(serviceId);
    }
    return updatedAtByServiceId[serviceId];
  };

  return entries.map(([serviceId, values]) => {
    const op: PatchBudgetServicesBatchOperation = {
      serviceId,
      valuePhase,
      values,
    };
    if (valuePhase === "negotiation" && negotiationVersion != null) {
      op.negotiationVersion = negotiationVersion;
    }
    const ifMatch = buildServiceIfMatch(lookupUpdatedAt(serviceId));
    if (ifMatch) {
      op.ifMatch = ifMatch;
    }
    return op;
  });
}

/** Split ops into chunks of at most {@link BUDGET_SERVICES_BATCH_MAX_OPS}. */
export function chunkBatchOperations(
  operations: PatchBudgetServicesBatchOperation[],
  maxOps: number = BUDGET_SERVICES_BATCH_MAX_OPS
): PatchBudgetServicesBatchOperation[][] {
  if (operations.length === 0) return [];
  const chunks: PatchBudgetServicesBatchOperation[][] = [];
  for (let i = 0; i < operations.length; i += maxOps) {
    chunks.push(operations.slice(i, i + maxOps));
  }
  return chunks;
}

/**
 * Clone for interim config sync after v2 batch (FE-P3-03):
 * v1 PUT **without** `budgetServices` so dual/external mode does not wipe
 * collection lines. Also strips `__v` / `isApiUpdate` like `BudgetService.update`.
 *
 * FE-P4-02: when `USE_SERVER_TOTALS` is on, callers skip this slim PUT after
 * pure batch/structural flushes (server totals already attached to write
 * responses). Config-only slim PUTs still go through
 * {@link BudgetService.update}, which strips totals trees.
 */
export function stripBudgetServicesForSlimPut(
  budget: Budget
): Omit<Budget, "budgetServices"> & { budgetServices?: never } {
  const {
    budgetServices: _services,
    __v: _version,
    isApiUpdate: _isApiUpdate,
    ...rest
  } = budget as Budget & { __v?: number };

  return rest;
}

/**
 * Se a linha pode ser apagada no servidor pelo seu `_id`.
 *
 * O FORMATO do id não responde essa pergunta. O reducer cria toda linha com
 * `uuid()`, e até as escritas v2 o array inteiro era gravado por PUT do
 * documento — então há orçamentos inteiros persistidos com uuid no `_id`, e o
 * BE casa por string (`findEmbeddedServiceById`), não por ObjectId. Exigir
 * ObjectId aqui fazia o DELETE ser pulado em silêncio justamente nessas linhas:
 * o fornecedor saía da `supplierList` pelo PUT enxuto, os serviços ficavam no
 * banco sem dono — invisíveis no editor, que renderiza a partir da
 * `supplierList`, e ainda somados nos totais.
 *
 * Quem responde "existe no servidor?" é a ORIGEM da linha: as candidatas a
 * DELETE saem sempre da baseline (`lastSavedServicesRef`), repinada só depois
 * de load ou save bem-sucedido. Linha criada localmente e ainda não persistida
 * nunca está lá — ela entra no diff como `create`.
 */
export function isDeletableServiceId(id: string | undefined | null): boolean {
  return typeof id === "string" && id.length > 0;
}

/**
 * O que fazer quando o DELETE de uma linha falha.
 *
 * - `already-deleted` (404): a linha já não está no servidor. O objetivo foi
 *   atingido, então o flush segue com a mesma limpeza local de um 204 — o
 *   delete do BE não é idempotente, e uma baseline defasada não pode derrubar
 *   o save inteiro.
 * - `version-conflict` (412): a linha mudou em outra sessão. Preserva o
 *   tratamento existente (linha restaurada + aviso) em vez de sobrescrever a
 *   edição alheia.
 * - `rewrite-whole-document`: falha de rede/servidor. Aqui NÃO dá para
 *   restaurar a linha e seguir: a exclusão do fornecedor é uma ação só, mas
 *   virou duas gravações (as linhas pelos endpoints v2, a `supplierList` pelo
 *   PUT enxuto). Com a linha de volta no estado local ela volta a bater com a
 *   baseline, o diff nunca mais propõe o DELETE, e o PUT de configuração
 *   seguinte grava o fornecedor removido deixando as linhas órfãs. O PUT do
 *   documento inteiro grava array e configuração de uma vez, do estado local —
 *   é a mesma saída que mover/transferir categoria já usa.
 */
export type ServiceDeleteFailure =
  | "already-deleted"
  | "version-conflict"
  | "rewrite-whole-document";

export type BudgetAutosaveTrigger =
  | "debounce"
  | "interval"
  | "navigation"
  | "manual";

/**
 * After a service-version 412, debounce and the periodic interval stay quiet.
 * An explicit save (`manual`) or leaving the screen (`navigation`) is allowed
 * and is what clears the hold.
 */
export function shouldFlushAutosave(
  trigger: BudgetAutosaveTrigger,
  heldForVersionConflict: boolean
): boolean {
  if (!heldForVersionConflict) return true;
  return trigger === "manual" || trigger === "navigation";
}

export function classifyServiceDeleteFailure(
  error: unknown
): ServiceDeleteFailure {
  const status = (
    error as
      | { response?: { status?: number; data?: { statusCode?: number } } }
      | undefined
  )?.response;
  const code = status?.status ?? status?.data?.statusCode;

  if (code === 404) return "already-deleted";
  if (code === 412) return "version-conflict";
  return "rewrite-whole-document";
}

/**
 * Hard delete (remove line from collection) vs soft version removal.
 * Soft removes keep the line and strip the active version from `versions[]`
 * — that still requires a full v1 PUT until BE supports versions PATCH.
 */
export function isHardServiceDelete(
  versionInfo: Pick<versionInfo, "version" | "status"> | undefined
): boolean {
  const version = versionInfo?.version ?? 0;
  const status = (versionInfo?.status || "OPEN").toUpperCase();
  return version === 0 && status !== "APPROVED" && status !== "APROVED";
}

/** Group identity key: calcOption + category (`type`) + supplierId. */
export function serviceGroupKey(
  service: Pick<ServiceData, "calcOption" | "type" | "supplierId">
): string {
  return `${service.calcOption}|${service.type}|${service.supplierId}`;
}

export type ServiceStructuralDiff = {
  creates: ServiceData[];
  deletes: ServiceData[];
  /** Same `_id`, different group identity → delete+create on flush. */
  moves: Array<{ before: ServiceData; after: ServiceData }>;
  /**
   * Same group identity but `versions` (or other non-value) changed, **or**
   * a delete/move whose `_id` is not a Mongo ObjectId (client UUID written
   * by an earlier full v1 PUT). v2 DELETE only accepts ObjectIds; slim PUT
   * omits `budgetServices` and would leave the line on Mongo.
   */
  needsFullDocumentPut: boolean;
};

/**
 * Diff service arrays for FE-P3-04 structural writes.
 * Ignores pure value-field edits (same id + same group + same versions).
 */
export function diffServiceStructuralChanges(
  before: ServiceData[] | undefined,
  after: ServiceData[] | undefined
): ServiceStructuralDiff {
  const beforeList = Array.isArray(before) ? before : [];
  const afterList = Array.isArray(after) ? after : [];

  const beforeById = new Map(
    beforeList.filter((s) => s?._id).map((s) => [String(s._id), s] as const)
  );
  const afterById = new Map(
    afterList.filter((s) => s?._id).map((s) => [String(s._id), s] as const)
  );

  const creates: ServiceData[] = [];
  const deletes: ServiceData[] = [];
  const moves: Array<{ before: ServiceData; after: ServiceData }> = [];
  let needsFullDocumentPut = false;

  for (const [id, afterSvc] of Array.from(afterById.entries())) {
    const beforeSvc = beforeById.get(id);
    if (!beforeSvc) {
      creates.push(afterSvc);
      continue;
    }

    if (serviceGroupKey(beforeSvc) !== serviceGroupKey(afterSvc)) {
      moves.push({ before: beforeSvc, after: afterSvc });
      continue;
    }

    const beforeVersions = JSON.stringify(beforeSvc.versions ?? []);
    const afterVersions = JSON.stringify(afterSvc.versions ?? []);
    if (beforeVersions !== afterVersions) {
      needsFullDocumentPut = true;
    }

    if ((beforeSvc.groupId ?? null) !== (afterSvc.groupId ?? null)) {
      needsFullDocumentPut = true;
    }
  }

  for (const [id, beforeSvc] of Array.from(beforeById.entries())) {
    if (!afterById.has(id)) {
      deletes.push(beforeSvc);
    }
  }

  return { creates, deletes, moves, needsFullDocumentPut };
}

/**
 * Build POST body for `createBudgetServiceV2` from an editor {@link ServiceData}.
 */
export function toCreateBudgetServiceBody(
  service: ServiceData,
  sortOrder?: number
): CreateBudgetServiceV2Body {
  const body: CreateBudgetServiceV2Body = {
    calcOption: service.calcOption as CalcOption,
    type: service.type,
    supplierId: service.supplierId,
    supplierName: service.supplierName,
    versions: service.versions ?? [0],
    groupId: service.groupId ?? null,
    budgetValues: service.budgetValues,
    negotiationValue: service.negotiationValue,
    closingValues: service.closingValues,
  };
  if (typeof sortOrder === "number") {
    body.sortOrder = sortOrder;
  }
  return body;
}
